import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MyMonthlyCalendar from '../components/my-availability/MyMonthlyCalendar';
import MyScheduleCard from '../components/my-availability/MyScheduleCard';
import MyTimeOffCard from '../components/my-availability/MyTimeOffCard';
import MyCoverageCard from '../components/my-availability/MyCoverageCard';
import PastTimeOffCard from '../components/my-availability/PastTimeOffCard';

export default function MyAvailability() {
  const navigate = useNavigate();
  const { user: currentUser, loading: authLoading } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) navigate('/login');
  }, [authLoading, currentUser, navigate]);

  const handleDataChanged = () => {
    setRefreshKey((prev) => prev + 1);
  };

  if (authLoading || !currentUser) {
    return (
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  const fullName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 'My';

  return (
    <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8 space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-100 lg:text-gray-800">
          My Availability
        </h1>
        <p className="text-sm text-gray-400 lg:text-gray-500 mt-1">
          {fullName} — set the days you work and add any upcoming time off.
        </p>
      </div>

      <section
        className="rounded-lg border border-sky-200 bg-sky-50 p-4 sm:p-5"
        aria-labelledby="availability-help-title"
      >
        <div className="flex items-start gap-3">
          <svg
            className="mt-0.5 h-5 w-5 flex-none text-sky-700"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" strokeWidth="2" />
            <path strokeLinecap="round" strokeWidth="2" d="M12 11v5m0-8h.01" />
          </svg>
          <div>
            <h2 id="availability-help-title" className="font-semibold text-sky-900">
              How to use My Availability
            </h2>
            <p className="mt-1 text-sm leading-6 text-sky-900">
              Start by setting and saving your normal weekly schedule in the
              <strong> Days of the Week</strong> section. After your weekly schedule is set, use the
              calendar to make changes for specific dates, such as working remotely or taking a
              custom day off. Use <strong>Upcoming Time Off</strong> to request vacation, sick, or
              personal time; requests must be approved before they affect your availability. You
              can review previous requests in <strong>Past Time Off</strong> at the bottom of the
              page.
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <MyScheduleCard onSaved={handleDataChanged} />
        <MyTimeOffCard onChange={handleDataChanged} />
      </div>

      <MyCoverageCard />

      <MyMonthlyCalendar refreshKey={refreshKey} />

      <PastTimeOffCard refreshKey={refreshKey} />
    </div>
  );
}
